import styled from "@emotion/styled"
import { Drawer, List, ListItemButton } from "@mui/material"
import { GlobalContext } from "common/GlobalContext"
import { useContext, useEffect, useState } from "react"
import { useSwipeable } from "react-swipeable"
import {
  finalBounceEase,
  menuTransitionTime,
  transition,
  transitionTime,
} from "theme/shared"

const Styled = {
  Drawer: styled(Drawer)`
    z-index: 10 !important;
    .MuiBackdrop-root {
      background-color: transparent;
    }
    .MuiDrawer-paperAnchorRight {
      width: 100%;
      box-shadow: none;
      background-color: transparent;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      position: relative;
      transition: none !important;
      transform: none !important;
      overflow: hidden;
    }
  `,
  MenuBackground: styled.div<{ active: boolean }>`
    position: absolute;
    width: 200vh;
    height: 200vh;
    border-radius: 50%;
    right: 0;
    top: 0;
    background-color: ${(p: any) => p.theme.colors.darkBlue};
    ${transition};
    opacity: 0;
    transform: translate3d(50%, -50%, 0) scale(0.1);
    ${(p: any) =>
      p.active &&
      `transform: translate3d(37%,-40%,0) scale(1);
        opacity: 1;
    `}
    ${(p: any) => p.theme.breakpoints.up("md")} {
      ${(p: any) =>
        p.active &&
        `transform: translate3d(37%,-40%,0) scale(2);
      `}
    }
  `,
  ListItem: styled(ListItemButton)<{ selected: boolean }>`
    text-transform: uppercase;
    font-size: 30px;
    text-align: center;
    padding: 10px 0;
    color: ${(p: any) => p.theme.colors.white45};
    font-weight: 400;
    justify-content: center;
    visibility: hidden;
    opacity: 0;

    ${transition}
    /* transition: all ${transitionTime}ms ${finalBounceEase},
      opacity ${menuTransitionTime}ms ease-in-out; */

    background-color: transparent !important;
    b {
      color: ${(p: any) => p.theme.colors.white};
      font-weight: bold;
    }

    ${(p: any) =>
      p.selected &&
      `
        transform: translate3d(0, 0, 0) !important;
        visibility: visible;
        opacity: 1;
    `}
    &:nth-child(1) {
      transform: translate3d(0, 600%, 0);
    }
    &:nth-child(2) {
      transform: translate3d(0, 500%, 0);
    }
    &:nth-child(3) {
      transform: translate3d(0, 400%, 0);
    }
    &:nth-child(4) {
      transform: translate3d(0, 200%, 0);
    }
    &:nth-child(5) {
      transform: translate3d(0, 100%, 0);
    }

    &.contact {
      flex-grow: 0;
      margin-top: 30px;
      transform: translate3d(0, 100%, 0);
      transition: all ${menuTransitionTime}ms
        ${transitionTime - menuTransitionTime}ms ${finalBounceEase};

      ${(p) =>
        !p.selected &&
        `transition: all ${menuTransitionTime}ms ${finalBounceEase};`}
    }
  `,
  Dot: styled.span`
    color: ${(p: any) => p.theme.colors.hotPink};
  `,
  Circle: styled.span`
    display: inline-block;
    width: 6px;
    height: 6px;
    border-radius: 3px;
    background-color: ${(p: any) => p.theme.colors.hotPink};
    margin-left: 0.1em;
    margin-bottom: -1px;
  `,
}

const Menu = () => {
  const { openMenu, setOpenMenu, sections } = useContext(GlobalContext)
  const [showBackground, setShowBackground] = useState(false)

  const handlers = useSwipeable({
    onSwipedUp: () => handleDrawerClose(),
  })

  useEffect(() => {
    let openTimeout
    if (openMenu) {
      openTimeout = setTimeout(() => {
        setShowBackground(true)
      }, 1)
    } else {
      setShowBackground(false)
    }

    return () => {
      clearTimeout(openTimeout)
    }
  }, [openMenu])

  const handleDrawerClose = () => setOpenMenu(false)

  // Determine which sections to show in menu
  // Main sections: home, services, clients, team (employees), contact
  // "Our Work" links to the first project section (index 3)
  const mainSections = sections.filter((section) => {
    const hash = section.hash
    return (
      hash === "home" ||
      hash === "services" ||
      hash === "clients" ||
      hash === "team" ||
      hash === "contact"
    )
  })

  // Add "Our Work" entry that links to first project (index 3)
  const menuItems = [
    ...mainSections.slice(0, 3), // home, services, clients
    {
      hash: "our-work",
      progressBarText: "Our Work",
    },
    ...mainSections.slice(3), // team, contact (if they exist)
  ]

  // Separate contact item (last item with special styling)
  const contactItem = menuItems.find((item) => item.hash === "contact")
  const regularItems = menuItems.filter((item) => item.hash !== "contact")

  return (
    <Styled.Drawer
      anchor="right"
      open={openMenu}
      onClose={handleDrawerClose}
      transitionDuration={transitionTime}
      className="menu"
    >
      <Styled.MenuBackground {...handlers} active={showBackground} />
      <List disablePadding>
        {regularItems.map((item) => {
          const isHome = item.hash === "home"
          return (
            <Styled.ListItem
              key={item.hash}
              selected={showBackground}
              onClick={handleDrawerClose}
              // @ts-ignore - MUI v7 ListItemButton component prop typing
              component="a"
              href={`#${item.hash}`}
            >
              {isHome ? (
                <b>
                  {item.progressBarText}
                  <Styled.Circle></Styled.Circle>
                </b>
              ) : (
                item.progressBarText
              )}
            </Styled.ListItem>
          )
        })}
      </List>

      {contactItem && (
        <Styled.ListItem
          selected={showBackground}
          className="contact"
          onClick={handleDrawerClose}
          // @ts-ignore - MUI v7 ListItemButton component prop typing
          component="a"
          href={`#${contactItem.hash}`}
        >
          <b>
            <Styled.Dot>{contactItem.progressBarText}</Styled.Dot>
          </b>
        </Styled.ListItem>
      )}
    </Styled.Drawer>
  )
}

export default Menu
